import { CloseCircleOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons'
import { Modal, Button, Divider, Upload } from 'gokwik-ui-kit'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { customRequest } from '@pages/onboarding/kyc-flow/utils'
import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { getDocIconByName } from '../../utils'
import { NON_OCR_DOCS } from '../../constants'
import { getDocumentDownloaded } from '@library/utilities/helpers/helper'

const DocsUploadModal = ({
    item,
    visible,
    onClose,
    doc,
    getDocumentList,
    onShowSampleClick,
    setIsUploading,
    setUploadedFileName,
    setOpenStatusModal,
    setUploadProgress,
    setOcrResult,
    ocrResult,
    setLoadingAI,
    setInputImageName,
    resolvedErrors,
    setResolvedErrors,
    handleDelete,
    showLowScoreAlert,
    setOcrApiError,
    handleImageChange,
    setUploadedFile,
    ocrApiError,
    setShowLowScoreAlert,
}) => {
    const userData = useSelector(getUserData)

    const handleUploadSuccess = (itemName: string) => {
        setResolvedErrors((prev) => [...prev, itemName])
    }

    const beforeUpload = (item, file) => {
        const isLt5M = file.size / 1024 / 1024 < 5
        const uploadedMimeType = file?.type
        const uploadFormat = uploadedMimeType.split('/')[1]
        const allowedFileTypes = item?.other_details?.allowed_format?.split(', ')
        const isAllowedFormat = allowedFileTypes?.includes(uploadFormat)

        if (!isAllowedFormat && allowedFileTypes !== undefined) {
            return false
        } else if (!isLt5M) {
            return false
        }
        return true
    }

    const customRequestHandler = async (options, item) => {
        const { onProgress, onSuccess, onError, file } = options

        setIsUploading(true)
        setUploadedFileName(file.name)
        setUploadedFile(file)
        setOpenStatusModal(true)
        setUploadProgress(0)

        try {
            const uploadWithProgress = (e) => {
                const percent = Math.round((e.loaded / e.total) * 100)
                setUploadProgress(percent)
                onProgress({ percent })
            }

            await customRequest(
                {
                    ...options,
                    onProgress: uploadWithProgress,
                },
                item,
                getDocumentList,
                () => {},
                () => {
                    setIsUploading(false)
                    setOpenStatusModal(false)
                    setUploadProgress(0)
                },
            )
            if (beforeUpload(item, file)) {
                setUploadProgress(100)
                onProgress({ percent: 100 })
                handleUploadSuccess(item.name)
                handleImageChange(file, item)
                setIsUploading(false)
                onClose()
            } else {
                setIsUploading(false)
                onClose()
                setUploadProgress(0)
                setOpenStatusModal(false)
            }
        } catch (error) {
            setIsUploading(false)
            setOpenStatusModal(false)
            onError(error)
            setUploadProgress(0)
        }
    }

    const handleClose = async () => {
        onClose()
        if (showLowScoreAlert || ocrApiError) {
            setShowLowScoreAlert(false)
            await handleDelete(doc)
        }
    }
    console.log(doc, 'doc')

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            width={500}
            closable={false}
            footer={null}
            wrapClassName='custom-modal'
            className='[&_.ant-modal-content]:p-0'
        >
            <div className='relative flex flex-col'>
                {/* <CloseCircleOutlined className='absolute top-0 right-0' /> */}
                <div className='flex justify-between items-start border-b mx-4 mt-4'>
                    <h2 className='text-lg font-semibold text-black'>{doc?.title}</h2>
                    <div className='flex flex-col gap-1'>
                        {doc?.show_preview_document && (
                            <a onClick={() => onShowSampleClick(item)} className='text-sm text-primary-500'>
                                View Sample Document
                            </a>
                        )}
                        {doc?.sample_download_url && (
                            <a
                                onClick={() => getDocumentDownloaded(doc?.sample_download_url, doc?.name)}
                                className='text-sm text-primary-500'
                            >
                                Download Template
                            </a>
                        )}
                    </div>
                </div>

                <Divider className='w-full' />

                <div className='bg-[#f5f5f5] border border-solid border-[#B4B4B4] rounded-md mx-4'>
                    <div className='flex justify-center items-center py-6'>
                        <img
                            src={getDocIconByName(doc?.name)}
                            alt={doc?.title}
                            className='h-48 w-auto object-contain'
                        />
                    </div>

                    <div className='rounded-md p-4 text-sm text-gray-700 m-4 bg-white'>
                        <p className='font-medium mb-2'>Instructions</p>
                        <ol className='list-decimal pl-5 space-y-1'>
                            {doc?.format_requirements?.map((requirement, index) => (
                                <li key={index}>{requirement}</li>
                            ))}
                        </ol>
                    </div>
                </div>

                <div className='w-full'>
                    <Upload
                        className='w-full [&_.ant-upload-select]:block [&_.ant-upload-select]:w-full'
                        onChange={() => {
                            logEvent(
                                doc?.name + '_' + 'upload_clicked',
                                'click',
                                'kyc',
                                userData?.userDetails?.email,
                                userData?.merchantDetails?.m_id,
                            )
                        }}
                        action={doc?.upload_url}
                        accept={doc?.other_details.allowed_format
                            ?.split(', ')
                            ?.map((format) => `.${format}`)
                            ?.join(', ')}
                        customRequest={(options) =>
                            customRequestHandler(options, {
                                ...doc,
                                field: doc?.name,
                            })
                        }
                        method='PUT'
                        listType='text'
                        maxCount={1}
                        showUploadList={false}
                    >
                        <Button
                            variant='primary'
                            className='w-[-webkit-fill-available] text-white font-medium rounded-md mt-5 h-10 mx-4'
                        >
                            <UploadOutlined /> Upload Document
                        </Button>
                    </Upload>
                    <Button
                        className='w-[-webkit-fill-available] font-medium rounded-md my-3 h-10 mx-4'
                        onClick={handleClose}
                    >
                        <CloseOutlined /> Cancel
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default DocsUploadModal
